import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { queryKeys } from '@/lib/queryKeys';

/** 학력 이력(V615 · 사용자 지시 2026-11-02). 서버 `PersonnelEducationService` 와 짝이다. */

export const EDUCATION_LEVELS = ['고졸', '초대졸', '대졸', '석사', '박사'] as const;

/** 고르개에 보이는 이름 — 저장 값은 승진 기준표와 같은 글자다. */
export const EDUCATION_LEVEL_LABELS: Record<string, string> = {
  고졸: '고졸',
  초대졸: '전문대(초대졸)',
  대졸: '대학(대졸)',
  석사: '석사',
  박사: '박사',
};

export type EducationRow = {
  id: string | null;
  level: string;
  schoolName: string | null;
  major: string | null;
  graduationYear: number | null;
  selfEntered: boolean;
};

export type EducationView = { rows: EducationRow[]; finalLevel: string | null; open: boolean };

export type School = { id: string; name: string; campus: string; kind: string | null; region: string | null };

/** `personnelId` 가 null 이면 본인(마이페이지)이다. */
function pathOf(personnelId: string | null): string {
  return personnelId ? `/v1/hr/personnel/${personnelId}/educations` : '/v1/me/educations';
}

export function useEducations(personnelId: string | null) {
  return useQuery({
    queryKey: ['educations', personnelId ?? 'me'],
    queryFn: () => apiFetch<EducationView>(pathOf(personnelId)),
  });
}

export function useSaveEducations(personnelId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { rows: EducationRow[]; open: boolean }) =>
      apiFetch<EducationView>(pathOf(personnelId), { method: 'PUT', body }),
    onSuccess: (view) => {
      queryClient.setQueryData(['educations', personnelId ?? 'me'], view);
      // 최종학력이 바뀌면 승진 판정·명부·직원찾기가 따라 바뀐다.
      void queryClient.invalidateQueries({ queryKey: ['mypage-career'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.directoryTree });
      void queryClient.invalidateQueries({ queryKey: queryKeys.directoryPerson });
      void queryClient.invalidateQueries({ queryKey: queryKeys.personnel });
    },
  });
}

export function useSchoolSearch(query: string) {
  const needle = query.trim();
  return useQuery({
    queryKey: ['schools', needle],
    queryFn: () => apiFetch<School[]>(`/v1/me/educations/schools?q=${encodeURIComponent(needle)}`),
    enabled: needle.length >= 1,
    staleTime: 5 * 60_000,
  });
}

export function schoolLabel(school: School): string {
  return school.campus && school.campus !== '본교' ? `${school.name} ${school.campus}` : school.name;
}
